import { useState, useCallback, useMemo, useEffect } from "react";
import {
  AppointmentDto,
  UserDto,
  SublocalityDto,
  TimeSlotDto,
  PatientAvailabilityDto,
  AppointmentsOfAllTypesDto,
} from "../../../api-client";
import { CalendarEvent } from "./adapters";
import { ClientTimeSlot, CalendarResource } from "./adapters/types";
import { useLocationId } from "../../../stores/User/User.hooks";
import {
  useUsersByLocation,
  useAppointmentsForDate,
} from "../../../stores/Calendar/calendar.queries";
import { useSaveAppointment } from "../../../stores/Calendar/calendar.mutations";
import { toCalendarEvents } from "./CalendarAdapters";
import { EventChangeArg, EventApi } from "@fullcalendar/core";
import { EventReceiveArg } from "@fullcalendar/interaction";
import moment from "moment";
import useStore from "../../../helpers/useStore";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";

// Data interface for routing calendar
export interface RoutingCalendarData {
  // Original schedule data (read-only)
  originalCalendarEvents: CalendarEvent[];

  // Modified schedule data (editable)
  modifiedCalendarEvents: CalendarEvent[];

  // Combined calendar events with proper resourceId assignments
  combinedCalendarEvents: CalendarEvent[];

  // Route planning specific data
  googleMapAppointments: AppointmentDto[];
  routeDurations: (number | null)[];
  routeplanUser?: UserDto;

  // Common data
  users: UserDto[];
  resources: CalendarResource[];
  selectedDate: Date;
  selectedDateStr: string;
  isLoading: boolean;

  // Patient availability (needed for route validation)
  patientAvailabilities: PatientAvailabilityDto[];
  patientUnavailableTypes: TimeSlotDto[];

  // UI state
  headerWidth: string;
  lockedAppointments: string[];
  sublocalities: SublocalityDto[];
}

// Handler interface for routing calendar
export interface RoutingCalendarHandlers {
  // Date handlers
  onDatesSet: (date: Date) => void;

  // Route planning specific handlers
  onRouteplanUserChange: (user: UserDto) => void;
  onSaveRouteplan: () => Promise<void>;
  onCloseRouteplan: () => void;

  // Event handlers (for modified schedule)
  onCreateEvent: (event: CalendarEvent) => Promise<void>;
  onChangeEvent: (eventChangeInfo: EventChangeArg) => Promise<void>;
  onEventReceive: (eventReceive: EventReceiveArg) => Promise<void>;
  onRemoveEvent: (id: string) => Promise<void>;
  onMoveEventDate: (event: CalendarEvent) => Promise<void>;
  onAddShelfEvent: (event: CalendarEvent) => Promise<void>;
  onEventContentClick: (event: ClientTimeSlot) => Promise<void>;

  // Validation handlers
  checkPatientAvailabilityForRoutePlanView: (
    event: EventApi,
    patientId: number
  ) => Promise<boolean>;

  // Utility handlers
  onOutdated: () => void;
  renderCalendar: () => Promise<void>;
}

/**
 * Hook for managing routing calendar state and logic.
 * Provides two calendar views: original schedule (read-only) and modified schedule (editable).
 * Handles route optimization and Google Maps integration.
 */
export const useRoutingController = (
  initialUser?: UserDto,
  initialDate?: Date
): {
  data: RoutingCalendarData;
  handlers: RoutingCalendarHandlers;
} | null => {
  // Core state
  const locationId = useLocationId();
  const CalendarStore = useStore(CalendarContext);
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate || new Date());
  const selectedDateStr = useMemo(() => moment(selectedDate).format("YYYY-MM-DD"), [selectedDate]);

  // Get the selected user from the store or find it in the users list
  const { data: fetchedUsers = [] } = useUsersByLocation(
    locationId,
    undefined,
    false,
    !!locationId
  );

  // lookup the userDTO for the routPlanningUserId
  const routeplanUser = useMemo(() => {
    if (!CalendarStore.routePlanningUserId) return undefined;
    return fetchedUsers.find((user) => user.id === CalendarStore.routePlanningUserId);
  }, [CalendarStore.routePlanningUserId, fetchedUsers]);

  // api expects an array of userIds
  const userIds = CalendarStore.routePlanningUserId ? [CalendarStore.routePlanningUserId] : [];

  // Route specific state
  const [googleMapAppointments, setGoogleMapAppointments] = useState<AppointmentDto[]>([]);
  const [routeDurations, setRouteDurations] = useState<(number | null)[]>([]);

  // UI state
  const drawerWidth = 320;
  const headerWidth = `calc(50% - ${drawerWidth / 2}px)`; // Split view layout

  // Fetch original appointments for the selected user and date
  const { data: originalAppointmentData, isLoading: appointmentsLoading } = useAppointmentsForDate(
    selectedDate,
    locationId!,
    true, // weeklyWorkDay
    { userIds },
    true,
    !!routeplanUser && !!locationId
  );

  // console.debug("Routplan query results", originalAppointmentData, appointmentsLoading, routeplanUser, locationId);

  const [modifiedAppointments, setModifiedAppointments] = useState<
    AppointmentsOfAllTypesDto | undefined
  >(originalAppointmentData);

  // Mutations
//   const { mutateAsync: saveAppointment } = useSaveAppointment();


// useEffect to set the modifiedAppointments back to original when originalAppointmentData changes
  useEffect(() => {
    // console.debug("Routplan originalAppointmentData changed", {
      // originalAppointmentData,
      // hasAppointments: !!originalAppointmentData?.appointments,
      // appointmentCount: originalAppointmentData?.appointments?.length || 0
    // });
    if (originalAppointmentData) {
        // copy of data:
        const copyOfData = JSON.parse(JSON.stringify(originalAppointmentData));
        // console.debug("Routplan resetting modified appointments to original", {
          // copyOfData,
          // appointmentCount: copyOfData.appointments?.length || 0
        // });
        setModifiedAppointments(copyOfData);
        // Also populate googleMapAppointments with the original appointments
        setGoogleMapAppointments(copyOfData.appointments || []);
    } else {
        // console.debug("Routplan originalAppointmentData is null/undefined, clearing states");
        setModifiedAppointments(undefined);
        setGoogleMapAppointments([]);
    }
  }, [originalAppointmentData]);

  // Generate resources for the selected user only
  const resources = useMemo((): CalendarResource[] => {
    if (!routeplanUser || !routeplanUser.id) return [];
    return [
                  {
                    id: '1_original',
                    title: 'Original',
                    extendedProps: {
                      userId: routeplanUser.id
                    }
                  },
                  {
                    id: '2_optimized', 
                    title: 'Neuer Plan',
                    extendedProps: {
                      userId: routeplanUser.id
                    }
                  }
                ];
  }, [routeplanUser]);

  // Convert appointments to calendar events
  const originalCalendarEvents = useMemo(() => {
    if (!originalAppointmentData) {
      // console.debug("Routplan originalCalendarEvents: no originalAppointmentData");
      return [];
    }
    const events = toCalendarEvents({
      appointments: originalAppointmentData.appointments || [],
      leaveOfAbsences: originalAppointmentData.leaveOfAbsences || [],
      eventTimeslots: originalAppointmentData.eventTimeSlots || [],
      continuousAppointments: [],
      continuousEventTimeSlots: [],
      lunchBreaks:  [], // TODO add lunch breaks
      continuousLunchBreaks: [], // TODO add continuous lunch breaks
      cancelledDays: [],
      travelTimes: originalAppointmentData.travelTimes || [],
      patientBlockingSlots: [],
      groupCheck: false,
      lockedAppointments: [],
      isContinuous: false,
      sublocalities: [],
    });
    // console.debug("Routplan originalCalendarEvents generated:", {
      // appointmentCount: originalAppointmentData.appointments?.length || 0,
      // eventCount: events.length,
      // events
    // });
    return events;
  }, [originalAppointmentData]);

  const modifiedCalendarEvents = useMemo(() => {
    if (!modifiedAppointments) {
      // console.debug("Routplan modifiedCalendarEvents: no modifiedAppointments");
      return [];
    }
    const events = toCalendarEvents({
      appointments: modifiedAppointments?.appointments || [],
      leaveOfAbsences: modifiedAppointments.leaveOfAbsences || [],
      eventTimeslots: modifiedAppointments.eventTimeSlots || [],
      continuousAppointments: [],
      continuousEventTimeSlots: [],
      lunchBreaks:  [], // TODO add lunch breaks
      continuousLunchBreaks: [], // TODO add continuous lunch breaks
      cancelledDays: [],
      travelTimes: modifiedAppointments.travelTimes || [],
      patientBlockingSlots: [],
      groupCheck: false,
      lockedAppointments: [],
      isContinuous: false,
      sublocalities: [],
    });
    // console.debug("Routplan modifiedCalendarEvents generated:", {
      // appointmentCount: modifiedAppointments?.appointments?.length || 0,
      // eventCount: events.length,
      // events
    // });
    return events;
  }, [ modifiedAppointments]);

  // Combined calendar events with resourceId assignments
  const combinedCalendarEvents = useMemo(() => {
    // Simply prefix original events' ids; leave modified events untouched for mutations.
    const originalWithResourceId = originalCalendarEvents.map(event => ({
      ...event,
      id: `orig_${event.id}`,
      resourceId: '1_original',
      extendedProps: {
        ...event.extendedProps,
        originalEventId: event.id,
        scheduleType: 'original'
      }
    }));

    const modifiedWithResourceId = modifiedCalendarEvents.map(event => ({
      ...event,
      resourceId: '2_optimized',
      extendedProps: {
        ...event.extendedProps,
        scheduleType: 'optimized'
      }
    }));

    const combined = [...originalWithResourceId, ...modifiedWithResourceId];

    // console.log('Combined calendar events (simple prefix originals):', {
      // originalCount: originalWithResourceId.length,
      // modifiedCount: modifiedWithResourceId.length,
      // totalCount: combined.length,
      // sampleOriginal: originalWithResourceId.slice(0, 3),
      // sampleModified: modifiedWithResourceId.slice(0, 3)
    // });

    return combined;
  }, [originalCalendarEvents, modifiedCalendarEvents]);

  // console.debug("Routplan combinedCalendarEvents updated",combinedCalendarEvents);

  // Handlers
  const handleDateChange = useCallback((date: Date) => {
    setSelectedDate(date);
  }, []);

  const handleRouteplanUserChange = useCallback(
    (user: UserDto) => {
      if (user.id) {
        CalendarStore.enableRoutePlanning(user.id);
        setGoogleMapAppointments([]);
        setModifiedAppointments(undefined);
      }
    },
    [CalendarStore]
  );

  const handleSaveRouteplan = useCallback(async () => {
    // if (!googleMapAppointments.length) return;

    // console.log("Saving route plan...", googleMapAppointments);
    // // TODO: Implement save logic
    // // This should update the appointments with the new schedule
    // try {
    //   // Save each modified appointment
    //   for (const appointment of googleMapAppointments) {
    //     // Update appointment times based on modified schedule
    //     if (appointment.id) {
    //       await saveAppointment({ id: appointment.id, appointment });
    //     }
    //   }
    // } catch (error) {
    //   console.error("Failed to save route plan:", error);
    // }
  }, [googleMapAppointments]);

  const handleCloseRouteplan = useCallback(() => {
    setGoogleMapAppointments([]);
    setModifiedAppointments(undefined);
    CalendarStore.disableRoutePlanning();
  }, [CalendarStore]);

  // Event handlers for modified calendar
  const handleEventChange = useCallback(
    async (eventChangeInfo: EventChangeArg) => {
    //   const event = eventChangeInfo.event as any as CalendarEvent;
    //   const appId = event.appointmentId || event.id;

    //   if (!appId) return;

    //   // Find and update the appointment in googleMapAppointments
    //   const app = googleMapAppointments?.find((a) => a.id === appId);
    //   if (!app || !app.timeSlots?.[0]) return;

    //   // Update the appointment time
    //   const updatedTimeSlot = {
    //     ...app.timeSlots[0],
    //     start: event.start,
    //     end: event.end,
    //     user: { id: event.resourceId || "" },
    //   };

    //   const updatedAppointment = {
    //     ...app,
    //     timeSlots: [updatedTimeSlot],
    //   };

    //   // Update both modified appointments and google map appointments
    //   const updatedAppointments = googleMapAppointments.map((a) =>
    //     a.id === appId ? updatedAppointment : a
    //   );

    //   // Sort by start time for route optimization
    //   const sortedApps = updatedAppointments.sort((a, b) => {
    //     const aStart = a.timeSlots?.[0]?.start || "";
    //     const bStart = b.timeSlots?.[0]?.start || "";
    //     return aStart.localeCompare(bStart);
    //   });

    //   setGoogleMapAppointments(sortedApps);
      //   setModifiedAppointments(sortedApps);
    },
    [googleMapAppointments]
  );

  const handleEventReceive = useCallback(
    async (eventReceiveInfo: EventReceiveArg) => {
    //   const draggedEvent = eventReceiveInfo.event;
    //   const appointmentId = draggedEvent.extendedProps?.appointmentId;

    //   if (!appointmentId) return;

    //   // Add the event to the google map appointments for route visualization
    //   const existingAppointment = originalAppointmentData?.appointments.find(
    //     (a: AppointmentDto) => a.id === appointmentId
    //   );
    //   if (existingAppointment) {
    //     setGoogleMapAppointments((prev) => {
    //       const exists = prev.some((a) => a.id === appointmentId);
    //       if (exists) return prev;
    //       return [...prev, existingAppointment];
    //     });
    //   }
    },
    [originalAppointmentData]
  );

  const checkPatientAvailabilityForRoutePlanView = useCallback(
    async (event: EventApi, patientId: number): Promise<boolean> => {
      // TODO: Implement patient availability checking for route planning
      // This should check if the patient is available at the proposed time
      return true;
    },
    []
  );

  // Create data object
  const data: RoutingCalendarData = useMemo(
    () => ({
      originalCalendarEvents,
      modifiedCalendarEvents,
      combinedCalendarEvents,
      googleMapAppointments,
      routeDurations,
      routeplanUser,
      users: fetchedUsers,
      resources,
      selectedDate,
      selectedDateStr,
      isLoading: appointmentsLoading,
      patientAvailabilities: [], // TODO: Add if needed
      patientUnavailableTypes: [], // TODO: Add if needed
      headerWidth,
      lockedAppointments: [],
      sublocalities: [],
    }),
    [
      combinedCalendarEvents,
      googleMapAppointments,
      routeDurations,
      routeplanUser,
      fetchedUsers,
      resources,
      selectedDate,
      selectedDateStr,
      appointmentsLoading,
      headerWidth,
    ]
  );

  // Create handlers object
  const handlers: RoutingCalendarHandlers = useMemo(
    () => ({
      onDatesSet: handleDateChange,
      onRouteplanUserChange: handleRouteplanUserChange,
      onSaveRouteplan: handleSaveRouteplan,
      onCloseRouteplan: handleCloseRouteplan,
      onCreateEvent: async () => {}, // Not needed for route planning
      onChangeEvent: handleEventChange,
      onEventReceive: handleEventReceive,
      onRemoveEvent: async () => {}, // Not needed for route planning
      onMoveEventDate: async () => {}, // Not needed for route planning
      onAddShelfEvent: async () => {}, // Not needed for route planning
      onEventContentClick: async () => {}, // Not needed for route planning
      checkPatientAvailabilityForRoutePlanView,
      onOutdated: () => {}, // Not needed for route planning
      renderCalendar: async () => {}, // Not needed for route planning
    }),
    [
      handleDateChange,
      handleRouteplanUserChange,
      handleSaveRouteplan,
      handleCloseRouteplan,
      handleEventChange,
      handleEventReceive,
      checkPatientAvailabilityForRoutePlanView,
    ]
  );

  return { data, handlers };
};
